/*
PURPOSE: User mention detection and validation utility for @ tagging in comments

USE CASES:
- Detect @mentions in user-generated text content
- Validate mentions against user alignments
- Extract mention information for rendering as clickable links
- Support @username patterns in comments

DOMAIN BOUNDARIES:
- Responsible for mention detection and validation only
- Does NOT handle rendering (handled by UserMention component)
- Does NOT store or persist mentions
- Does NOT handle autocomplete (handled by useMentionDetection hook)

ASSUMPTIONS:
- Input text may contain @mentions mixed with regular text
- Mentions should be made clickable and link to user profiles
- Only aligned/connected users can be mentioned (validated on backend)
- Mention pattern: @username (alphanumeric + underscores)
*/

import { DetectedItem, segmentText } from './urlProcessor';

// Regex pattern for @mentions
// Matches @username where username is alphanumeric plus underscores/hyphens
// Must be preceded by whitespace or start of string to avoid matching email addresses
const MENTION_REGEX = /(?:^|[\s])@([\w-]+)/g;

export interface MentionInfo extends DetectedItem {
  username: string;
  userId?: string; // Resolved user ID (if validated)
  displayName?: string; // User's display name
  isValid: boolean; // Whether this mention maps to a real user
}

/**
 * Detects @mentions in text and returns information about each mention found
 * Note: This only detects the syntax, validation happens when resolved with user data
 */
export function detectMentions(text: string): MentionInfo[] {
  const mentions: MentionInfo[] = [];
  const matches = text.matchAll(MENTION_REGEX);

  for (const match of matches) {
    if (match.index === undefined) continue;

    const username = match[1]; // The captured username (without @)
    const fullMatch = match[0]; // Full match including @ and potential leading whitespace

    // Calculate the actual position of the @ symbol
    const atSymbolIndex = match.index + (fullMatch.startsWith('@') ? 0 : 1);
    const startIndex = atSymbolIndex;
    const endIndex = startIndex + username.length + 1; // +1 for the @ symbol

    mentions.push({
      username,
      startIndex,
      endIndex,
      isValid: false // Will be set to true when resolved with user data
    });
  }

  return mentions;
}

/**
 * Resolves detected mentions against a list of users (typically user's alignments)
 * Updates the mention info with user IDs and display names
 */
export interface UserAlignment {
  targetUserId: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
}

export function resolveMentions(
  mentions: MentionInfo[],
  users: UserAlignment[]
): MentionInfo[] {
  return mentions.map(mention => {
    // Try to find a matching user by display name or username
    const matchedUser = users.find(user => {
      const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return displayName.toLowerCase() === mention.username.toLowerCase();
    });

    if (matchedUser) {
      return {
        ...mention,
        userId: matchedUser.targetUserId,
        displayName: matchedUser.displayName || `${matchedUser.firstName || ''} ${matchedUser.lastName || ''}`.trim(),
        isValid: true
      };
    }

    return mention;
  });
}

/**
 * Text segment that can contain mentions
 */
export interface MentionTextSegment {
  text: string;
  isMention: boolean;
  mentionInfo?: MentionInfo;
}

/**
 * Processes text and returns segments with mention information
 * Useful for rendering mixed text/mention content
 */
export function processTextWithMentions(text: string, resolvedMentions?: MentionInfo[]): MentionTextSegment[] {
  const mentions = resolvedMentions || detectMentions(text);

  return segmentText<MentionInfo, MentionTextSegment>(
    text,
    mentions,
    (mentionInfo, itemText) => ({
      text: itemText,
      isMention: true,
      mentionInfo
    }),
    (plainText) => ({
      text: plainText,
      isMention: false
    })
  );
}

/**
 * Extracts valid user IDs from mentioned users in text
 * Useful for backend notification targeting
 */
export function extractMentionedUserIds(text: string, users: UserAlignment[]): string[] {
  const mentions = detectMentions(text);
  const resolvedMentions = resolveMentions(mentions, users);

  return resolvedMentions
    .filter(m => m.isValid && m.userId)
    .map(m => m.userId!);
}

/**
 * Validates if a string looks like a mention pattern
 * Useful for autocomplete triggering
 */
export function isMentionPattern(text: string): boolean {
  return /@[\w-]*$/.test(text);
}

/**
 * Extracts the current mention being typed (for autocomplete)
 * Returns the partial username after the @ symbol
 */
export function getCurrentMention(text: string, cursorPosition: number): string | null {
  // Look backwards from cursor to find @ symbol
  let atPosition = -1;
  for (let i = cursorPosition - 1; i >= 0; i--) {
    if (text[i] === '@') {
      atPosition = i;
      break;
    }
    // Stop if we hit whitespace (not part of mention)
    if (/\s/.test(text[i])) {
      break;
    }
  }

  if (atPosition === -1) {
    return null;
  }

  // Extract text between @ and cursor
  const mentionText = text.slice(atPosition + 1, cursorPosition);

  // Only return if it looks like a valid username (alphanumeric + underscores/hyphens)
  if (/^[\w-]*$/.test(mentionText)) {
    return mentionText;
  }

  return null;
}
